import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { CreateTenantFormData } from '../schemas/createTenantSchema'

interface CreateTenantResult {
  tenant: {
    id: string
    name: string
    slug: string
    schema_name: string
  }
  admin: {
    userId: string
    email: string
    temporaryPassword?: string
    isNewUser: boolean
  }
}

export function useCreateTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateTenantFormData): Promise<CreateTenantResult> => {
      const schemaName = `tenant_${data.slug.replace(/-/g, '_')}`

      // Step 1: Insert tenant into tenants table
      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .insert({
          name: data.name,
          slug: data.slug,
          schema_name: schemaName,
          status: 'active',
          domain: data.domain || null,
          logo_url: data.logoUrl || null,
        })
        .select()
        .single()

      if (tenantError) {
        if (tenantError.code === '23505') {
          // Unique violation
          if (tenantError.message?.includes('slug')) {
            throw new Error('Este identificador já está em uso')
          }
          if (tenantError.message?.includes('schema_name')) {
            throw new Error('Este identificador já está em uso')
          }
        }
        throw new Error(`Erro ao criar empresa: ${tenantError.message}`)
      }

      // Step 2: Create tenant schema with RLS
      const { error: schemaError } = await supabase.rpc('create_tenant_schema_with_rls', {
        p_tenant_id: tenant.id,
        p_schema_name: schemaName,
      })

      if (schemaError) {
        // Rollback: delete the tenant
        await supabase.from('tenants').delete().eq('id', tenant.id)
        throw new Error(`Erro ao criar schema: ${schemaError.message}`)
      }

      // Step 3: Call Edge Function to create admin user
      const { data: sessionData } = await supabase.auth.getSession()
      if (!sessionData.session) {
        await supabase.from('tenants').delete().eq('id', tenant.id)
        throw new Error('Session expired. Please log in again.')
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

      const response = await fetch(`${supabaseUrl}/functions/v1/create-tenant-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          tenantId: tenant.id,
          email: data.adminEmail,
          name: data.adminName || undefined,
        }),
      })

      const adminResult = await response.json()

      if (!response.ok) {
        // Rollback: delete the tenant (cascade will clean up schema)
        await supabase.from('tenants').delete().eq('id', tenant.id)
        throw new Error(adminResult.error || 'Error creating administrator')
      }

      if (adminResult?.error) {
        // Rollback: delete the tenant (cascade will clean up schema)
        await supabase.from('tenants').delete().eq('id', tenant.id)
        throw new Error(adminResult.error)
      }

      return {
        tenant: {
          id: tenant.id,
          name: tenant.name,
          slug: tenant.slug,
          schema_name: tenant.schema_name,
        },
        admin: {
          userId: adminResult.userId,
          email: adminResult.email,
          temporaryPassword: adminResult.temporaryPassword,
          isNewUser: adminResult.isNewUser,
        },
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
    },
  })
}
